<template>
    <div ref="wrapperEl" class="flex flex-col gap-6">
        <vx-box class="overflow-clip">
            <div
                class="top-0 z-10 flex flex-col gap-3 border-solid border-t-0 border-x-0 border-b border-b-slate-200 bg-white p-6 [@media(min-height:700px)]:sticky"
            >
                <div class="flex items-start sm:items-center justify-between gap-2">
                    <div class="text-xl font-extrabold leading-none">
                        {{ activeTab === 'all' ? 'Select Resources' : 'Manage Selection' }}
                    </div>
                    <vx-button color="muted" @click="cancel()">
                        <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                    </vx-button>
                </div>
                <vx-tabs v-model="activeTab" class="bg-slate-100!">
                    <vx-tab value="all">All</vx-tab>
                    <vx-tab value="selected">Selected ({{ selectedResources.length }})</vx-tab>
                </vx-tabs>
                <vx-input
                    v-if="activeTab === 'all'"
                    ref="searchEl"
                    placeholder="Search"
                    v-model="search"
                    @keydown.enter="handleEnter"
                    @update:modelValue="searchFn(props.fetchParams, $event)"
                >
                    <template v-slot:right>
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass" class="text-sm"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
            <div v-if="activeTab == 'all'" class="flex flex-col gap-2 bg-slate-50 p-6">
                <template v-if="options.length > 0">
                    <analytics-select-resource-item
                        v-for="option in options.slice(0, 10)"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :key="`resource_item_${option.resource_type}_${option.resource_id}_all`"
                        :resource="option"
                        :search="search"
                        :selected="isSelected(option)"
                        @selected="toggleResource(option)"
                    />
                </template>

                <div v-else class="flex p-8 items-center justify-center flex-col gap-3">
                    <template v-if="loadingOptions">
                        <vx-icon-loading spin class="w-6 h-6"></vx-icon-loading>
                    </template>
                    <template v-else>
                        <font-awesome-icon widthAuto :icon="faSquareDashed" class="text-2xl"></font-awesome-icon>
                        <span class="font-normal text-lg">No results found</span>
                    </template>
                </div>
            </div>
            <div v-if="activeTab === 'selected'" class="flex flex-col gap-2 bg-slate-50 p-6">
                <template v-if="selectedResources.length > 0">
                    <analytics-select-resource-item
                        v-for="resource in selectedResources"
                        :key="`resource_item_${resource.resource_type}_${resource.resource_id}_selected`"
                        :orgUnitId="props.orgUnitId"
                        :groupId="props.groupId"
                        :resource="resource"
                        :selected="isSelected(resource)"
                        @selected="toggleResource(resource)"
                        :openDefault="true"
                    />
                </template>

                <div v-else class="flex p-8 items-center justify-center flex-col gap-3">
                    <font-awesome-icon widthAuto :icon="faSquareDashed" class="text-2xl"></font-awesome-icon>
                    <span class="font-normal text-lg">No selected resources</span>
                </div>
            </div>
        </vx-box>

        <vx-sticky-footer class="w-full max-w-lg mx-auto">
            <vx-button class="grow" @click="apply()">Done with selection</vx-button>
        </vx-sticky-footer>
    </div>
</template>

<script setup>
import AnalyticsSelectResourceItem from './AnalyticsSelectResourceItem.vue';
import { faMagnifyingGlass, faSquareDashed, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxInput, VxButton, VxTabs, VxTab, VxStickyFooter, VxIconLoading } from '@voxie/frontend-components';
import { nextTick, onMounted, ref, watch } from 'vue';
import { useAnalyticsSelectResources } from '~/components/analytics/partial/select-resources/useAnalyticsSelectResources';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const { selectingResources, selectedResources, loadingOptions, options, searchFn, isSameResource } =
    useAnalyticsSelectResources();

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: false,
    },
    groupId: {
        type: String,
        required: false,
    },
    fetchParams: {
        type: Object,
        required: true,
    },
    modelValue: {
        type: Array,
        required: false,
    },
});

const emit = defineEmits(['update:modelValue']);

onMounted(async () => {
    await nextTick();
    // initialize with empty search
    searchFn(props.fetchParams, search.value);
});

watch(
    () => props.fetchParams,
    async () => {
        searchFn(props.fetchParams, search.value);
    },
    {
        deep: true,
    }
);

watch(
    () => props.modelValue,
    (newValue) => {
        selectedResources.value = newValue ? [...newValue] : [];
    },
    {
        deep: true,
        immediate: true,
    }
);

const cancel = () => {
    selectedResources.value = props.modelValue ? [...props.modelValue] : [];
    selectingResources.value = false;
};

const apply = () => {
    emit(
        'update:modelValue',
        selectedResources.value.length
            ? selectedResources.value.map(({ resource_type, resource_id }) => ({
                  resource_type,
                  resource_id,
              }))
            : undefined
    );
    selectingResources.value = false;
};

const activeTab = ref('all'); // all | selected

const isSelected = (resource) => {
    return selectedResources.value.some((currentResource) => isSameResource(currentResource, resource));
};

const wrapperEl = ref();
watch(selectingResources, async () => {
    await nextTick();
    if (selectingResources.value && wrapperEl.value) {
        const rect = wrapperEl.value.getBoundingClientRect();
        window.scrollTo({
            top: window.scrollY + rect.top - 80,
            behavior: 'smooth',
        });
        searchEl.value?.field?.focus?.();
    } else {
        search.value = '';
    }
});

const handleEnter = () => {
    if (!search.value.length) {
        return;
    }

    if (loadingOptions.value) {
        return;
    }

    if (options.value[0]) {
        toggleResource(options.value[0]);
    }
};

const toggleResource = (resource) => {
    if (isSelected(resource)) {
        selectedResources.value = selectedResources.value.filter(
            (currentResource) => !isSameResource(currentResource, resource)
        );
        return;
    }

    if (selectedResources.value.length >= 10) {
        toasted.error('You can only select up to 10 resources.');
        return;
    }
    selectedResources.value.push(resource);
};

const searchEl = ref();
const search = ref('');

defineExpose({
    apply,
});
</script>
